// Shared-drive <select> plus its loading/error/empty states, used by every caller that
// lets a user choose the shared drive Drive sync reads from.
import { useEffect } from "react";
import type { UseQueryResult } from "react-query";

import type { SharedDrive } from "../api/hooks/useDriveSync";
import styles from "./DriveSyncSettingsCard.module.css";

// The shared drive InRange's project and global files live in, chosen by default.
const DEFAULT_SHARED_DRIVE = "InRange";

interface Props {
  query: UseQueryResult<{ drives: SharedDrive[] }>;
  id: string;
  value: string;
  onChange: (driveId: string) => void;
  placeholder: string;
  emptyMessageSubject: string;
}

export const SharedDrivePicker = ({
  query,
  id,
  value,
  onChange,
  placeholder,
  emptyMessageSubject,
}: Props) => {
  const drives = query.data?.drives;

  useEffect(() => {
    if (value || !drives) return;
    const preferred =
      drives.find((d) => d.name.toLowerCase() === DEFAULT_SHARED_DRIVE.toLowerCase()) ??
      (drives.length === 1 ? drives[0] : undefined);
    if (preferred) onChange(preferred.id);
  }, [value, drives, onChange]);

  if (query.isLoading) {
    return <p className={styles.muted}>Loading your shared drives…</p>;
  }

  if (query.isError) {
    return (
      <p className={styles.error}>
        {(query.error as Error)?.message || "Couldn't load your shared drives."}{" "}
        <button type="button" onClick={() => void query.refetch()} className={styles.linkButton}>
          Retry
        </button>
      </p>
    );
  }

  if (!drives || drives.length === 0) {
    return (
      <p className={styles.muted}>
        Your Google account can&apos;t open any shared drives. Ask for access to the shared
        drive {emptyMessageSubject} files are in, then reload.
      </p>
    );
  }

  return (
    <select
      id={id}
      className={styles.input}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {drives.map((d) => (
        <option key={d.id} value={d.id}>
          {d.name}
        </option>
      ))}
    </select>
  );
};
